import { Axon, SocketController } from '@visiba/axon';
import { Injectable } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { HostingService } from '@application/hosting.service';
import { devApplicationLocalStorage } from '@application/application_storage_local_dev';
import { DateService } from '@application/time/date.service';
import { ChatCommunicator, ChatCommunicatorMetadata } from './chat_communicator';
import { Logger } from '@visiba/logger';
import { AnalyticService } from '@application/analytic/analytic.service';
import { Injector } from '@visiba/instantiation';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import type { ChatCommunicatorProtocol } from './chat_communicator_protocol';

interface Presentation {
	communicator: ChatCommunicator | null;
	isReadyForCommunication: boolean;
}

@Injectable()
export class ChatController {
	public readonly presentation = Presentation.create<Presentation>({
		communicator: null,
		isReadyForCommunication: false,
	});
	private socket: Axon | null = null;
	private connectedTo: string | null = null;
	private readonly logger = new Logger('ChatController');

	constructor(
		private readonly hostingService: HostingService,
		private readonly dateService: DateService,
		private readonly analyticService: AnalyticService,
		private readonly injector: Injector,
		private readonly unhandledIssuesService: UnhandledIssuesService,
	) {
		// Empty
	}

	public join(connectId: string, metaData: ChatCommunicatorMetadata, communicatorProtocol: ChatCommunicatorProtocol): void {
		if (this.connectedTo === connectId) {
			this.logger.logInformation('tried to join an already joined room');

			return;
		}

		this.connectedTo = connectId;

		let controller: SocketController;
		if (communicatorProtocol.controllerInstance == null) {
			controller = this.createSocket().getController('messaging');
		} else {
			controller = communicatorProtocol.controllerInstance;
			this.presentation.write({
				isReadyForCommunication: true,
			});
		}

		const { communicator: currentCommunicator } = this.presentation.read();
		currentCommunicator?.destroy();

		const communicator = new ChatCommunicator(
			communicatorProtocol,
			metaData.issuesTrackedById ?? null,
			connectId,
			metaData,
			this.dateService,
			controller,
			this.analyticService,
			this.injector,
			this.unhandledIssuesService,
		);

		this.presentation.write({
			communicator,
		});
	}

	public removeCurrentBridge(): void {
		const { communicator: currentBridge } = this.presentation.read();
		currentBridge?.destroy();
		this.connectedTo = null;
	}

	public terminate(): void {
		this.removeCurrentBridge();

		this.socket
			?.close()
			.then(() => {
				this.presentation.write({
					isReadyForCommunication: false,
				});
			})
			.catch(() => {
				// noop
			});

		this.socket = null;
	}

	public sendMessage(message: string | null, fileTickets: string[]): void {
		const { communicator: currentBridge } = this.presentation.read();
		if (currentBridge == null) return;

		currentBridge.sendMessage(message, fileTickets);
	}

	private createSocket(): Axon {
		if (this.socket == null) {
			this.socket = new Axon(this.hostingService.wsBaseUri, (message: string) => {
				this.logger.logVerbose('{message}', message);
			});
			const devSession = devApplicationLocalStorage.get('identitySession');
			this.socket.setParameters({
				AuthorizationTicket: devSession,
			});
			this.socket.events.on('onReconnecting', () => {
				this.presentation.write({
					isReadyForCommunication: false,
				});
			});
			this.socket.events.on('onReconnected', () => {
				const bridge = this.presentation.read().communicator;
				bridge?.getAllMessages();

				this.presentation.write({
					isReadyForCommunication: true,
				});
			});

			// The creation of microtask is intended to return to higher priority work.
			queueMicrotask(() => {
				this.socket
					?.open()
					.then(() => {
						this.presentation.write({
							isReadyForCommunication: true,
						});
					})
					.catch(() => {
						this.socket = null;
						this.connectedTo = null;
					});
			});
		} else {
			this.presentation.write({
				isReadyForCommunication: true,
			});
		}

		return this.socket;
	}
}
